import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Paper, IconButton, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Switch, ButtonBase, CircularProgress,
} from "@mui/material";
import Dialog from "../components/AppDialog";
import { AccessTime, Block, CheckCircle, ChevronLeft, RadioButtonUnchecked, Visibility } from "@mui/icons-material";
import MemberPicker from "../components/MemberPicker";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import api from "../api/axios";
import { useDarkMode } from "../App";
import { useDialog } from "../contexts/DialogProvider";
import PullToRefresh from "react-simple-pull-to-refresh";
import { monthParam } from "../hooks/useCalendarUrlState";
import { hhmm } from "../utils/timeFormat";

const practiceRooms = ["Lesson 1", "Lesson 2", "Lesson 3", "Lesson 4", "Lesson 5", "Lesson 6"];
const dayNames = ["일", "월", "화", "수", "목", "금", "토"];

function getEndTime(start, duration = 30) {
  if (!start) return "";
  let [h, m] = start.split(":").map(Number);
  m += duration;
  h += Math.floor(m / 60);
  m = m % 60;
  if (h > 23) h = 23;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function PracticeDetailPage({ user }) {
  const { darkMode } = useDarkMode();
  const { toast } = useDialog();
  const { date } = useParams();
  const navigate = useNavigate();
  // 뒤로 가면 이 날짜가 속한 달의 연습실 달력으로 (이번 달이면 파라미터 없음)
  const backMonth = dayjs(date).isValid() ? monthParam(dayjs(date).year(), dayjs(date).month()) : null;
  const practiceBackUrl = backMonth ? `/practice?month=${backMonth}` : "/practice";
  const [reservations, setReservations] = useState([]);
  const [members, setMembers] = useState([]);
  const [practiceConfig, setPracticeConfig] = useState(null);
  const [effectiveConfig, setEffectiveConfig] = useState(null);
  const [dialog, setDialog] = useState({ open: false, selectedUserId: null, reservationType: "practice" });
  const [userViewMode, setUserViewMode] = useState(false);
  const [blockMode, setBlockMode] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState(() => new Set());
  const [blocking, setBlocking] = useState(false);
  const dragRef = useRef({ dragging: false, mode: "add", touchKey: null, longPressTimer: null, touchDragging: false, startX: 0, startY: 0 });
  const gridRef = useRef(null);

  useEffect(() => {
    fetchPracticeConfig();
  }, []);

  useEffect(() => {
    if (date) {
      fetchEffectiveConfig(date);
      api.get("/practices", { params: { date } })
        .then(res => setReservations(res.data))
        .catch(() => setReservations([]));
    }
  }, [date]);

  useEffect(() => {
    if (user.role === "ADMIN") {
      api.get("/members").then(res => setMembers(res.data)).catch(() => setMembers([]));
    }
  }, [user.role]);

  const fetchPracticeConfig = async () => {
    try {
      const res = await api.get("/practice-config");
      setPracticeConfig(res.data);
    } catch (err) {
      setPracticeConfig({
        start_time: "12:00",
        end_time: "18:00",
        slot_duration: 30,
        lesson_restricted_rooms: ["Lesson 1", "Lesson 2"],
        lesson_restricted_times: ["12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00"],
        lesson_restricted_days: [6],
      });
    }
  };

  const fetchEffectiveConfig = async (date) => {
    try {
      const res = await api.get("/practice-config/effective", { params: { date } });
      setEffectiveConfig(res.data);
    } catch (err) {
      console.error("유효 설정 로드 실패:", err);
    }
  };

  const generateTimeSlots = () => {
    const config = effectiveConfig || practiceConfig;
    if (!config) return [];

    const slots = [];

    const startTime = effectiveConfig?.start_time;
    const endTime = effectiveConfig?.end_time;

    if (!startTime || !endTime) return [];

    const normalize = (t) => t.length > 5 ? t.slice(0, 5) : t;

    const [startH, startM] = normalize(startTime).split(":").map(Number);
    const [endH, endM] = normalize(endTime).split(":").map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const duration = config.slot_duration || 30;

    for (let m = startMinutes; m < endMinutes; m += duration) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const timeStr = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
      slots.push(timeStr);
    }

    return slots;
  };

  function isTimeUnavailable(time) {
    const unavailableTimes = effectiveConfig?.unavailable_times || [];
    return unavailableTimes.includes(time);
  }

  function isRoomUnavailable(room, time = null) {
    // 전체 시간대에 사용 불가능한 연습실 (레거시)
    const unavailableRooms = effectiveConfig?.unavailable_rooms || [];
    if (unavailableRooms.includes(room)) return true;

    // 시간대별 사용 불가능한 연습실
    if (time) {
      const unavailableRoomsByTime = effectiveConfig?.unavailable_rooms_by_time || {};
      const unavailableRoomsForTime = unavailableRoomsByTime[time] || [];
      if (unavailableRoomsForTime.includes(room)) return true;
    }

    return false;
  }

  // 관리자가 차단 모드에서 이 날짜에 한해 해제한 고정 차단 슬롯
  function isReleasedSlot(room, time) {
    const releasedSlots = effectiveConfig?.released_slots || [];
    return releasedSlots.some(s => s.room_name === room && (s.start_time || "").slice(0, 5) === time);
  }

  // 고정 차단(주간 설정 기반)이면서 이 날짜에 해제되지 않은 슬롯
  function isFixedBlockedSlot(room, time) {
    return (isTimeUnavailable(time) || isRoomUnavailable(room, time)) && !isReleasedSlot(room, time);
  }

  // 차단 모드: 마우스 드래그가 그리드 밖에서 끝나도 종료되도록 전역에서 감지
  useEffect(() => {
    const endDrag = () => { dragRef.current.dragging = false; };
    window.addEventListener("pointerup", endDrag);
    return () => window.removeEventListener("pointerup", endDrag);
  }, []);

  // 차단 모드 터치 드래그: 길게 누르면 드래그 선택 시작, 그 동안 스크롤을 막고
  // 손가락 아래의 셀을 elementFromPoint로 찾아 선택한다.
  // preventDefault가 필요하므로 non-passive 네이티브 리스너로 등록 (React 터치 이벤트는 passive)
  useEffect(() => {
    if (!blockMode) return;
    const el = gridRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      const d = dragRef.current;
      if (d.touchDragging) {
        e.preventDefault();
        const t = e.touches[0];
        const target = document.elementFromPoint(t.clientX, t.clientY);
        const cell = target && target.closest ? target.closest("[data-slot]") : null;
        if (cell) applySlotSelection(cell.dataset.slot, d.mode);
      } else if (d.longPressTimer != null) {
        const t = e.touches[0];
        // 길게 누르기 전에 일정 거리 이상 움직이면 스크롤 제스처로 판단
        if (Math.abs(t.clientX - d.startX) > 10 || Math.abs(t.clientY - d.startY) > 10) {
          clearTimeout(d.longPressTimer);
          d.longPressTimer = null;
          d.touchKey = null;
        }
      }
    };
    const onTouchEnd = () => {
      const d = dragRef.current;
      if (d.longPressTimer != null) {
        clearTimeout(d.longPressTimer);
        d.longPressTimer = null;
      }
      d.touchDragging = false;
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [blockMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // 예약 목록이 갱신되면 그 사이 일반 예약이 생긴 슬롯은 선택에서 제거 (빈 칸·차단 칸 선택은 유지)
  useEffect(() => {
    setSelectedSlots(prev => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter(key => {
        const [room, time] = key.split("|");
        return !reservations.some(r => r.room_name === room && r.start_time.slice(0, 5) === time && !r.is_block);
      }));
      return next.size === prev.size ? prev : next;
    });
  }, [reservations]);

  const slotKey = (room, time) => `${room}|${time}`;

  function applySlotSelection(key, mode) {
    setSelectedSlots(prev => {
      const has = prev.has(key);
      if (mode === "add" ? has : !has) return prev;
      const next = new Set(prev);
      if (mode === "add") next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function handleBlockPointerDown(e, room, time) {
    const key = slotKey(room, time);
    const d = dragRef.current;
    if (e.pointerType === "mouse") {
      e.preventDefault();
      d.dragging = true;
      d.mode = selectedSlots.has(key) ? "remove" : "add";
      d.touchKey = null;
      applySlotSelection(key, d.mode);
    } else {
      // 터치: 짧은 탭 = 단일 토글, 길게 누르면 드래그 범위 선택 시작, 스크롤이 시작되면 무효화
      d.touchKey = key;
      d.startX = e.clientX;
      d.startY = e.clientY;
      d.mode = selectedSlots.has(key) ? "remove" : "add";
      if (d.longPressTimer != null) clearTimeout(d.longPressTimer);
      d.longPressTimer = setTimeout(() => {
        d.longPressTimer = null;
        d.touchDragging = true;
        d.touchKey = null;
        applySlotSelection(key, d.mode);
        if (navigator.vibrate) navigator.vibrate(15);
      }, 350);
    }
  }

  function handleBlockPointerEnter(room, time) {
    if (dragRef.current.dragging) {
      applySlotSelection(slotKey(room, time), dragRef.current.mode);
    }
  }

  function handleBlockPointerUp(e, room, time) {
    const d = dragRef.current;
    if (e.pointerType === "mouse") return;
    if (d.longPressTimer != null) {
      clearTimeout(d.longPressTimer);
      d.longPressTimer = null;
    }
    if (d.touchKey === slotKey(room, time)) {
      const key = d.touchKey;
      d.touchKey = null;
      applySlotSelection(key, selectedSlots.has(key) ? "remove" : "add");
    }
    d.touchDragging = false;
  }

  function toggleBlockMode(on) {
    setBlockMode(on);
    setSelectedSlots(new Set());
    if (on) setUserViewMode(false);
  }

  // 선택된 슬롯을 '차단할 빈 칸' / '해제할 임시 차단' / '해제할 고정 차단'으로 분류
  function getSelectionInfo() {
    const toBlock = [];
    const releaseIds = [];
    const releaseFixed = [];
    selectedSlots.forEach(key => {
      const [room, time] = key.split("|");
      const r = reservations.find(rr => rr.room_name === room && rr.start_time.slice(0, 5) === time);
      if (r) {
        if (r.is_block) releaseIds.push(r.id);
      } else if (isFixedBlockedSlot(room, time)) {
        releaseFixed.push({ room_name: room, start_time: time });
      } else {
        toBlock.push({ room, time });
      }
    });
    return { toBlock, releaseIds, releaseFixed };
  }

  async function handleBlockSelected() {
    const { toBlock } = getSelectionInfo();
    if (toBlock.length === 0 || blocking) return;
    const duration = practiceConfig?.slot_duration || 30;
    const slots = toBlock.map(({ room, time }) => ({
      room_name: room,
      start_time: time,
      end_time: getEndTime(time, duration),
    }));
    setBlocking(true);
    try {
      const res = await api.post("/practices/block", { date, slots });
      const { blocked, skipped } = res.data;
      let msg = `${blocked}개 슬롯을 차단했습니다.`;
      if (skipped?.length > 0) msg += ` (이미 예약된 ${skipped.length}개는 제외)`;
      toast(msg, skipped?.length > 0 ? "warning" : "success");
      setSelectedSlots(new Set());
      const listRes = await api.get("/practices", { params: { date } });
      setReservations(listRes.data);
    } catch (err) {
      toast(err?.response?.data?.message || "차단 실패", "error");
    } finally {
      setBlocking(false);
    }
  }

  async function handleUnblockSelected() {
    const { releaseIds, releaseFixed } = getSelectionInfo();
    if (releaseIds.length + releaseFixed.length === 0 || blocking) return;
    setBlocking(true);
    try {
      let released = 0;
      let alreadyGone = 0;
      if (releaseIds.length > 0) {
        const res = await api.post("/practices/unblock", { ids: releaseIds });
        released += res.data.released;
        alreadyGone += res.data.requested - res.data.released;
      }
      if (releaseFixed.length > 0) {
        const res = await api.post("/practice-config/release-slots", { date, slots: releaseFixed });
        released += res.data.released;
      }
      let msg = `${released}개 차단을 해제했습니다.`;
      if (alreadyGone > 0) msg += ` (${alreadyGone}개는 이미 해제됨)`;
      toast(msg, alreadyGone > 0 ? "warning" : "success");
      setSelectedSlots(new Set());
      await fetchEffectiveConfig(date);
      const listRes = await api.get("/practices", { params: { date } });
      setReservations(listRes.data);
    } catch (err) {
      toast(err?.response?.data?.message || "차단 해제 실패", "error");
    } finally {
      setBlocking(false);
    }
  }

  const startTimes = generateTimeSlots();

  // 평일 당일 예약 제한 안내 문구 (PracticePage.getWeekdayRestrictionMessage와 동일 규칙)
  // 아직 예약이 열리지 않은 평일이면 문구를, 예약 가능하면 null을 반환
  function getWeekdayRestrictionMessage() {
    if (process.env.REACT_APP_TEST_BYPASS_WEEKDAY_CHECK === 'true') return null;
    const dow = dayjs(date).day();
    if (dow < 1 || dow > 5) return null;
    if (!effectiveConfig) return null;

    const applySameDayOnly = effectiveConfig.weekday_same_day_only !== false;
    const applyBookingTime = effectiveConfig.weekday_time_restricted !== false;
    const startHour = effectiveConfig.weekday_booking_start_hour ?? 12;

    const koreaTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const koreaTodayStr = koreaTime.toISOString().slice(0, 10);
    const isToday = date === koreaTodayStr;

    if ((applySameDayOnly && !isToday) || (applyBookingTime && koreaTime.getHours() < startHour)) {
      return `평일 연습실은 당일 ${startHour}시 이후에 예약이 가능합니다.`;
    }
    return null;
  }

  const weekdayRestrictionMessage = getWeekdayRestrictionMessage();
  // 회원(또는 사용자 관점 모드)에게만 제약을 적용 — 관리자/선생님 본인 예약은 서버도 제한하지 않음
  const weekdayBlocked = !!weekdayRestrictionMessage && (user.role === "MEMBER" || userViewMode);

  function isLessonRoomRestricted(room, time, applyUserView = false) {
    // 관리자 / 선생님은 제한 없음 (사용자 관점 모드가 아닐 때)
    if ((user?.role === "ADMIN" || user?.role === "TEACHER") && !applyUserView) return false;

    if (!effectiveConfig?.lesson_restricted) return false;

    const restrictedRooms = effectiveConfig.lesson_restricted_rooms || [];
    const restrictedTimes = effectiveConfig.lesson_restricted_times || [];

    // 방 또는 시간이 제한 대상이 아니면 통과
    if (!restrictedRooms.includes(room)) return false;
    if (!restrictedTimes.includes(time)) return false;

    // 본인 예약은 예외
    const myReservation = reservations.find(
      r =>
        r.room_name === room &&
        r.start_time.slice(0, 5) === time &&
        r.user_id === user.id
    );

    if (myReservation) return false;

    return true;
  }


  function getReservation(room, time) {
    return reservations.find(r => r.room_name === room && r.start_time.slice(0, 5) === time);
  }

  function handleCellClick(room, time, reservation) {
    if (isLessonRoomRestricted(room, time)) return;

    // 평일 당일 예약 제한 중인 빈 칸은 목록 페이지와 동일하게 토스트로 안내
    if (weekdayBlocked && !reservation) {
      toast(weekdayRestrictionMessage, "info");
      return;
    }

    const isMine = reservation && reservation.user_id === user.id;
    const isAdmin = user.role === "ADMIN";
    
    if (!isAdmin && reservation && reservation.autogenerated) return;
    if (!isAdmin && reservation && !isMine) return;
    
    setDialog({
      open: true, room, time, reservation, isMine, isAdmin,
      action: reservation ? "cancel" : "reserve",
      selectedUserId: isAdmin && !reservation ? null : user.id,
      reservationType: "practice",
    });
  }

  function closeDialog() { setDialog(d => ({ ...d, open: false })); }
  function resetDialog() { setDialog({ open: false, selectedUserId: null, reservationType: "practice" }); }

  const handleRefresh = useCallback(async () => {
    await fetchPracticeConfig();
    if (date) {
      await fetchEffectiveConfig(date);
      try {
        const res = await api.get("/practices", { params: { date } });
        setReservations(res.data);
      } catch {
        setReservations([]);
      }
    }
    if (user.role === "ADMIN") {
      try {
        const res = await api.get("/members");
        setMembers(res.data);
      } catch {
        setMembers([]);
      }
    }
  }, [date, user.role]);

  async function handleDialogAction() {
    const { room, time, reservation, action, selectedUserId, reservationType } = dialog;
    const slotDuration = practiceConfig?.slot_duration || 30;

    if (action === "reserve") {
      if (user.role === "ADMIN" && !selectedUserId) {
        toast("예약할 회원을 선택해주세요.", "warning");
        return;
      }

      try {
        await api.post("/practices", {
          room_name: room,
          date: date,
          start_time: time,
          end_time: getEndTime(time, slotDuration),
          user_id: user.role === "ADMIN" ? selectedUserId : undefined,
          autogenerated: user.role === "ADMIN" && reservationType === "lesson",
        });
        closeDialog();
        api.get("/practices", { params: { date } }).then(res => setReservations(res.data));
        
        if (user.role === "ADMIN" && selectedUserId !== user.id) {
          const selectedMember = members.find(m => m.id === selectedUserId);
          const label = reservationType === "lesson" ? "레슨" : "연습실";
          toast(`${selectedMember?.name || "회원"}님의 ${label} 예약이 완료되었습니다.`);
        }
      } catch (err) {
        toast(err?.response?.data?.message || "예약 실패", "error");
      }
    } else if (action === "cancel" && reservation) {
      try {
        await api.delete(`/practices/${reservation.id}`);
        closeDialog();
        api.get("/practices", { params: { date } }).then(res => setReservations(res.data));
      } catch (err) {
        toast(err?.response?.data?.message || "취소 실패", "error");
      }
    } else {
      closeDialog();
    }
  }

  function getReservationStyle(reservation, isMine) {
    if (!reservation) return null;
    if (isMine) return { bgColor: "#c8e6c9", textColor: "#1b5e20", borderColor: "#4caf50", displayText: "내 예약", chipLabel: "내 예약", chipBg: "#4caf50", chipTextColor: "#fff" };
    if (reservation.autogenerated) return { bgColor: "#e8d5f2", textColor: "#6a1b9a", borderColor: "#9c27b0", displayText: reservation.user?.name?.trim() ? `${reservation.user.name}님` : "예약됨", chipLabel: "레슨", chipBg: "#9c27b0", chipTextColor: "#fff" };
    return { bgColor: "#ffeeee", textColor: "#c62828", borderColor: "#f44336", displayText: reservation.user?.name?.trim() ? `${reservation.user.name}님` : "예약됨", chipLabel: "연습실", chipBg: "#f44336", chipTextColor: "#fff" };
  }

  if (!practiceConfig || !effectiveConfig) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  const slotDuration = practiceConfig?.slot_duration || 30;
  const blockSelection = blockMode ? getSelectionInfo() : { toBlock: [], releaseIds: [], releaseFixed: [] };
  const releaseCount = blockSelection.releaseIds.length + blockSelection.releaseFixed.length;

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isPullable={!blockMode}
      pullingContent={
        <Box sx={{ textAlign: "center", py: 2, color: darkMode ? "#aaa" : "#666" }}>
          ↓ 당겨서 새로고침
        </Box>
      }
      refreshingContent={
        <Box sx={{ textAlign: "center", py: 2, color: "primary.main" }}>
          새로고침 중...
        </Box>
      }
    >
    {/* 차단 모드에서는 하단 고정 액션 바가 마지막 행을 가리지 않도록 여백 확보 */}
    <Box sx={{ pt: 1, pb: blockMode ? 12 : 2, maxWidth: 900, mx: "auto", px: { xs: 0.5, sm: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <IconButton onClick={() => navigate(practiceBackUrl)} edge="start" sx={{ mr: 0.5 }}><ChevronLeft /></IconButton>
          <Typography fontWeight="bold" sx={{ whiteSpace: "nowrap", fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{dayjs(date).format("YYYY년 ")}</Box>
            {dayjs(date).format("MM월 DD일")} ({dayNames[dayjs(date).day()]})
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 2 }, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ width: { xs: 16, sm: 24 }, height: { xs: 16, sm: 24 }, bgcolor: "#e8d5f2", borderRadius: 1, border: "2px solid #9c27b0" }} />
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.9rem" } }}>레슨</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ width: { xs: 16, sm: 24 }, height: { xs: 16, sm: 24 }, bgcolor: "#ffeeee", borderRadius: 1, border: "2px solid #f44336" }} />
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.9rem" } }}>연습실</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ width: { xs: 16, sm: 24 }, height: { xs: 16, sm: 24 }, bgcolor: "#c8e6c9", borderRadius: 1, border: "2px solid #4caf50" }} />
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.9rem" } }}>내 예약</Typography>
          </Box>
        </Box>
      </Box>

      {(user.role === "ADMIN" || user.role === "TEACHER") && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: { xs: 1.25, sm: 2 }, mb: 1 }}>
          {user.role === "ADMIN" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Block fontSize="small" color={blockMode ? "error" : "disabled"} />
              <Typography variant="body2" color={blockMode ? "error" : "text.secondary"} sx={{ whiteSpace: "nowrap" }}>
                차단 모드
              </Typography>
              <Switch
                size="small"
                color="error"
                checked={blockMode}
                onChange={(e) => toggleBlockMode(e.target.checked)}
              />
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Visibility fontSize="small" color={userViewMode ? "primary" : "disabled"} />
            <Typography variant="body2" color={userViewMode ? "primary" : "text.secondary"} sx={{ whiteSpace: "nowrap" }}>
              사용자 관점
            </Typography>
            <Switch
              size="small"
              checked={userViewMode}
              onChange={(e) => {
                setUserViewMode(e.target.checked);
                if (e.target.checked) toggleBlockMode(false);
              }}
            />
          </Box>
        </Box>
      )}

      {blockMode && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, mb: 1.5, borderRadius: 2, bgcolor: darkMode ? "rgba(244, 67, 54, 0.15)" : "#ffebee" }}>
          <Block fontSize="small" sx={{ color: darkMode ? "#ef9a9a" : "#c62828" }} />
          <Typography variant="body2" sx={{ color: darkMode ? "#ef9a9a" : "#c62828", fontSize: { xs: "0.78rem", sm: "0.875rem" } }}>
            탭으로 선택, 길게 누른 채 드래그하면 범위 선택 — 빈 칸은 차단, 차단된 칸은 해제됩니다.
          </Typography>
        </Box>
      )}

      {weekdayBlocked && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, mb: 1.5, borderRadius: 2, bgcolor: darkMode ? "rgba(255, 243, 224, 0.15)" : "#fff3e0" }}>
          <AccessTime fontSize="small" sx={{ color: darkMode ? "#ffb74d" : "#e65100" }} />
          <Typography variant="body2" sx={{ color: darkMode ? "#ffb74d" : "#e65100", fontSize: { xs: "0.78rem", sm: "0.875rem" } }}>
            {weekdayRestrictionMessage}
          </Typography>
        </Box>
      )}

      {effectiveConfig.is_override && effectiveConfig.has_time_override && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: darkMode ? "rgba(255, 243, 224, 0.15)" : "#fff3e0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="예외 설정 적용" size="small" color="warning" />
            <Typography variant="body2">
              {hhmm(effectiveConfig.start_time)} ~ {hhmm(effectiveConfig.end_time)}
              {!effectiveConfig.lesson_restricted && " | 레슨실 제한 없음"}
            </Typography>
          </Box>
          {effectiveConfig.override_reason && (
            <Typography variant="caption" color="text.secondary">
              사유: {effectiveConfig.override_reason}
            </Typography>
          )}
        </Paper>
      )}

      <Paper ref={gridRef} elevation={2} sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: `40px repeat(${practiceRooms.length}, minmax(42px, 1fr))`,
              sm: `50px repeat(${practiceRooms.length}, 1fr)`
            },
            borderBottom: "1px solid #eee",
            textAlign: "center"
          }}
        >
          <Box sx={{ fontWeight: "bold", p: { xs: 0.3, sm: 0.6 }, bgcolor: darkMode ? "grey.800" : "#f7f7f7", borderRight: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #eee", fontSize: { xs: "0.6rem", sm: "0.85rem" } }}>시간</Box>
          {practiceRooms.map(room => (
            <Box key={room} sx={{ fontWeight: "bold", p: { xs: 0.3, sm: 0.6 }, borderRight: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #eee", bgcolor: darkMode ? "grey.800" : "#f7f7f7", fontSize: { xs: "0.6rem", sm: "0.85rem" } }}>{room}</Box>
          ))}
        </Box>

        {startTimes.map(time => (
          <Box
            key={time}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: `40px repeat(${practiceRooms.length}, minmax(42px, 1fr))`,
                sm: `50px repeat(${practiceRooms.length}, 1fr)`
              },
              borderTop: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #eee"
            }}
          >
            <Box
              sx={{
                p: { xs: 0.15, sm: 0.3 },
                bgcolor: darkMode ? "grey.900" : "#fafafa",
                borderRight: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #eee",
                fontSize: { xs: "0.55rem", sm: "0.7rem" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 36,
                lineHeight: 1.2
              }}
            >
              <div>{time}</div>
              <div style={{ fontSize: "0.5rem", color: darkMode ? "#aaa" : "#666" }}>~</div>
              <div>{getEndTime(time, slotDuration)}</div>
            </Box>

            {practiceRooms.map(room => {
              const reservation = getReservation(room, time);
              const isMine = reservation && reservation.user_id === user.id;
              const isRealAdmin = user.role === "ADMIN" || user.role === "TEACHER";
              const isAdmin = isRealAdmin && !userViewMode;
              const isRestricted = isLessonRoomRestricted(room, time, userViewMode);
              const isReleased = isReleasedSlot(room, time);
              const isTimeBlocked = isTimeUnavailable(time) && !isReleased;
              const isRoomBlocked = isRoomUnavailable(room, time) && !isReleased;
              const isBlockCell = !!(reservation && reservation.is_block);
              const isFixedBlocked = !reservation && (isTimeBlocked || isRoomBlocked);
              // 차단 모드에서는 빈 칸(차단)·임시 차단 칸(해제)·고정 차단 칸(고정 해제) 모두 선택 가능
              const isSelectable = blockMode && user.role === "ADMIN" && !userViewMode && (!reservation || isBlockCell);
              const isReleaseCandidate = isBlockCell || isFixedBlocked;
              const isSelected = isSelectable && selectedSlots.has(slotKey(room, time));

              let displayContent;
              let displayStyle = { bgColor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd", textColor: darkMode ? "#e0e0e0" : "#222", borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0" };
              let canClick = true;

              // 관리자 임시 차단 — 관리자 본인 명의 예약이므로 '내 예약'보다 먼저 판정
              if (isBlockCell) {
                canClick = user.role === "ADMIN" && !userViewMode;
                // 차단 모드에서 선택된 차단 칸 = 해제 후보 (파란 틴트)
                displayStyle = isSelected
                  ? { bgColor: darkMode ? "rgba(33, 150, 243, 0.25)" : "#bbdefb", textColor: darkMode ? "#90caf9" : "#1565c0", borderColor: "#2196f3" }
                  : { bgColor: darkMode ? "grey.800" : "#fafafa", textColor: darkMode ? "#666" : "#999", borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0" };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>사용 불가</Typography>
                    {isRealAdmin && !userViewMode && (
                      <Typography fontSize={{ xs: "0.55rem", sm: "0.65rem" }}>(임시)</Typography>
                    )}
                  </Box>
                );
              } else if (isFixedBlocked && isSelectable) {
                // 차단 모드: 고정 차단 칸 — 이 날짜에 한해 해제 가능 (파란 체크 = 해제 후보)
                displayStyle = isSelected
                  ? { bgColor: darkMode ? "rgba(33, 150, 243, 0.25)" : "#bbdefb", textColor: darkMode ? "#90caf9" : "#1565c0", borderColor: "#2196f3" }
                  : { bgColor: darkMode ? "grey.800" : "#fafafa", textColor: darkMode ? "#666" : "#999", borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0" };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>사용 불가</Typography>
                    <Typography fontSize={{ xs: "0.55rem", sm: "0.65rem" }}>(고정)</Typography>
                  </Box>
                );
              // 본인 예약은 사용 불가 시간/연습실이더라도 우선 표시
              } else if (isMine) {
                const reservationStyle = getReservationStyle(reservation, isMine);
                canClick = true;
                displayStyle = { bgColor: reservationStyle.bgColor, textColor: reservationStyle.textColor, borderColor: reservationStyle.borderColor };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>{reservationStyle.displayText}</Typography>
                    <Chip label={reservationStyle.chipLabel} size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: reservationStyle.chipBg, color: reservationStyle.chipTextColor, fontWeight: 700 }} />
                  </Box>
                );
              } else if ((isTimeBlocked || isRoomBlocked) && !isAdmin) {
                canClick = false;
                displayStyle = { bgColor: darkMode ? "grey.800" : "#fafafa", textColor: darkMode ? "#666" : "#999", borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0" };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>사용 불가</Typography>
                  </Box>
                );
              } else if (isRestricted) {
                canClick = false;
                displayStyle = { bgColor: darkMode ? "grey.800" : "#f5f5f5", textColor: darkMode ? "#888" : "#757575", borderColor: darkMode ? "rgba(255,255,255,0.2)" : "#bdbdbd" };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>레슨실</Typography>
                  </Box>
                );
              } else if (reservation) {
                const reservationStyle = getReservationStyle(reservation, isMine);
                canClick = isMine || isAdmin;
                displayStyle = { bgColor: reservationStyle.bgColor, textColor: reservationStyle.textColor, borderColor: reservationStyle.borderColor };
                displayContent = (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }} fontWeight={600}>{reservationStyle.displayText}</Typography>
                    <Chip label={reservationStyle.chipLabel} size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: reservationStyle.chipBg, color: reservationStyle.chipTextColor, fontWeight: 700 }} />
                  </Box>
                );
              } else {
                // 차단 모드에서 선택된 빈 칸 = 차단 후보 (빨간 틴트)
                if (isSelected) {
                  displayStyle = { bgColor: darkMode ? "rgba(244, 67, 54, 0.25)" : "#ffcdd2", textColor: darkMode ? "#ef9a9a" : "#c62828", borderColor: "#f44336" };
                }
                displayContent = <Typography fontSize={{ xs: "0.6rem", sm: "0.75rem" }}>예약 가능</Typography>;
              }

              return (
                <Box
                  key={room + time}
                  sx={{
                    p: { xs: 0.3, sm: 0.5 },
                    textAlign: "center",
                    borderRight: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #eee",
                    bgcolor: displayStyle.bgColor,
                    color: displayStyle.textColor,
                    fontSize: { xs: "0.6rem", sm: "0.8rem" },
                    cursor: canClick ? "pointer" : "default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: { xs: 40, sm: 50 },
                    border: `1px solid ${displayStyle.borderColor}`,
                    transition: "background-color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease",
                    // scale 확대는 맨 오른쪽 열이 그리드 밖으로 넘쳐 가로 스크롤을 만들므로 사용하지 않음
                    "&:hover": canClick || isSelectable ? { opacity: 0.8, boxShadow: darkMode ? "inset 0 0 8px rgba(255,255,255,0.1)" : "inset 0 0 8px rgba(0,0,0,0.1)" } : {},
                    wordBreak: "break-word",
                    userSelect: blockMode ? "none" : undefined,
                    WebkitUserSelect: blockMode ? "none" : undefined,
                    WebkitTouchCallout: blockMode ? "none" : undefined,
                    position: "relative",
                    ...(isSelectable ? { cursor: "pointer" } : {})
                  }}
                  data-slot={isSelectable ? slotKey(room, time) : undefined}
                  onPointerDown={isSelectable ? (e) => handleBlockPointerDown(e, room, time) : undefined}
                  onPointerEnter={isSelectable ? () => handleBlockPointerEnter(room, time) : undefined}
                  onPointerUp={isSelectable ? (e) => handleBlockPointerUp(e, room, time) : undefined}
                  onPointerCancel={isSelectable ? () => {
                    const d = dragRef.current;
                    d.touchKey = null;
                    if (d.longPressTimer != null) { clearTimeout(d.longPressTimer); d.longPressTimer = null; }
                  } : undefined}
                  onContextMenu={isSelectable ? (e) => e.preventDefault() : undefined}
                  onClick={() => { if (isSelectable) return; canClick && handleCellClick(room, time, reservation); }}
                >
                  {displayContent}
                  {isSelectable && (
                    isSelected ? (
                      <CheckCircle sx={{ position: "absolute", top: 2, right: 2, fontSize: { xs: 12, sm: 14 }, color: isReleaseCandidate ? "#2196f3" : "#f44336" }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ position: "absolute", top: 2, right: 2, fontSize: { xs: 12, sm: 14 }, color: darkMode ? "#888" : "#90a4ae" }} />
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Paper>

      {blockMode && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: "calc(68px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 3,
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mr: 0.5 }}>
            {selectedSlots.size}개 선택
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={selectedSlots.size === 0 || blocking}
            onClick={() => setSelectedSlots(new Set())}
          >
            취소
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={releaseCount === 0 || blocking}
            onClick={handleUnblockSelected}
          >
            해제 {releaseCount}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={blockSelection.toBlock.length === 0 || blocking}
            onClick={handleBlockSelected}
          >
            차단 {blockSelection.toBlock.length}
          </Button>
        </Paper>
      )}

      <Dialog open={dialog.open} onClose={closeDialog} TransitionProps={{ onExited: resetDialog }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
            {dialog.action === "reserve" ? "연습실 예약" : dialog.reservation?.is_block ? "차단 해제" : "예약 취소"}
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
            {dayjs(date).format("YYYY년 MM월 DD일")} ({dayNames[dayjs(date).day()]}) · {dialog.room}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "#FAFAF7" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
              <Typography variant="body2" color="text.secondary">시간</Typography>
              <Typography variant="body2" fontWeight={600}>{dialog.time} ~ {getEndTime(dialog.time, slotDuration)}</Typography>
            </Box>
            {dialog.reservation && dialog.isAdmin && (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                  <Typography variant="body2" color="text.secondary">예약자</Typography>
                  <Typography variant="body2" fontWeight={600}>{dialog.reservation.user?.name || "예약자"}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                  <Typography variant="body2" color="text.secondary">예약 타입</Typography>
                  <Typography variant="body2" fontWeight={600}>{dialog.reservation.is_block ? "차단 (임시)" : dialog.reservation.autogenerated ? "레슨" : "연습실"}</Typography>
                </Box>
              </>
            )}
          </Box>

          {dialog.isAdmin && dialog.action === "reserve" && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>예약 타입</Typography>
              <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: 2.5, overflow: "hidden" }}>
                {[{ value: "practice", label: "연습실" }, { value: "lesson", label: "레슨" }].map(opt => {
                  const selected = dialog.reservationType === opt.value;
                  return (
                    <ButtonBase
                      key={opt.value}
                      onClick={() => setDialog(d => ({ ...d, reservationType: opt.value }))}
                      sx={{
                        flex: 1, py: 1.1, fontSize: "0.9rem",
                        fontWeight: selected ? 600 : 400,
                        bgcolor: selected ? "primary.main" : "transparent",
                        color: selected ? "primary.contrastText" : "text.secondary",
                        transition: "background-color 0.15s ease, color 0.15s ease",
                      }}
                    >
                      {opt.label}
                    </ButtonBase>
                  );
                })}
              </Box>

              <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>예약할 회원</Typography>
              <MemberPicker
                members={members}
                selectedId={dialog.selectedUserId}
                onSelect={(id) => setDialog(d => ({ ...d, selectedUserId: id }))}
                selfOption={{ id: user.id, label: "관리자 본인" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>관리자 권한으로 예약합니다</Typography>
            </>
          )}

          {dialog.isAdmin && dialog.action === "cancel" && !dialog.isMine && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>다른 사용자의 예약을 취소합니다</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={closeDialog} variant="outlined" color="inherit" sx={{ flex: 1 }}>
            {dialog.action === "reserve" ? "취소" : "닫기"}
          </Button>
          <Button
            onClick={handleDialogAction}
            variant="contained"
            color={dialog.action === "reserve" ? "primary" : "error"}
            disabled={dialog.isAdmin && dialog.action === "reserve" && !dialog.selectedUserId}
            sx={{ flex: 2 }}
          >
            {dialog.action === "reserve"
              ? (dialog.isAdmin
                  ? (dialog.selectedUserId
                      ? (dialog.selectedUserId === user.id
                          ? "본인으로 예약"
                          : `${members.find(m => m.id === dialog.selectedUserId)?.name || "회원"} 회원으로 예약`)
                      : "회원을 선택하세요")
                  : "예약")
              : dialog.reservation?.is_block ? "차단 해제" : "예약 취소"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PullToRefresh>
  );
}

export default PracticeDetailPage;
